import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CookieConsentContext from '../../contexts/CookieConsentContext';
import { supportChatConsent } from '../../utils/cookieConsentStorage';
import MarketingHeader from './MarketingHeader';
import MarketingFooter from './MarketingFooter';
import AuthSlimFooter from './AuthSlimFooter';
import { MarketingShellContext } from './MarketingContext';
import { isAdaptivePath } from './adaptivePaths';

import './MarketingLayout.css';
import './MarketingAdaptiveTheme.css';

/**
 * Hero pages render a full-bleed hero that slides UP under the transparent
 * header (the glow bleeds to the top). On these the header floats transparent
 * over the hero and only gains its blur on scroll.
 *
 * NON-hero pages (login, signup, support, skills, posts, etc.) have no hero
 * behind the header — so a transparent header just shows the flat shell as a
 * detached "black strip". For those, the header is ALWAYS solid (integrated,
 * like a normal site header). This list is the single source of truth.
 */
const HERO_PATH_PREFIXES = [
  '/ai-employees', '/super-agents', '/agent-to-agent',
  '/hermes', '/cursor', '/claude-code', '/codex',
  '/linear', '/openclaw', '/story',
];
const isHeroPath = (pathname) =>
  pathname === '/' || HERO_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Auth pages (/login, /signup) are conversion surfaces: they get a SLIM shell —
 * header without the marketing nav (Log in / Sign up buttons stay) and a
 * one-line legal footer instead of the full sitemap footer. PostHog showed
 * mid-form visitors leaking away through nav links, and zero footer usage.
 */
const AUTH_PATHS = new Set(['/login', '/signup']);

/**
 * SYSTEM-ADAPTIVE marketing pages (Nitish, Aug 23): these follow the
 * visitor's OS theme — the blog's model (light face + #161618 dark face,
 * plain screen, no WebGL field). Batch 1 (Aug 23 pm, his ruling) extended
 * the set from Home to the core marketing pages; the LIST lives in
 * adaptivePaths.js — one module consumed by this layout, CookieBanner AND
 * ThemeContext, so they can never drift. /login + /signup joined as the
 * batch-1 follow-on (CEL-1627): the shell rides this list like any page,
 * and the form INTERNALS flip via ThemeContext (adaptive auth paths follow
 * the visitor instead of the dark pin).
 */

/**
 * MarketingLayout — the logged-out marketing shell: top header, no sidebar.
 * DARK-LOCKED by default (the `dark` class on the root), with ONE exception
 * (Nitish, Aug 23): adaptivePaths.js pages are SYSTEM-ADAPTIVE — they follow
 * the visitor's OS theme via html[data-mkt-theme] (set pre-paint by the boot
 * script in public/index.html) and MarketingAdaptiveTheme.css, which remaps
 * the token VALUES under the same markup. The `dark` class stays on the root
 * either way; adaptive pages simply reinterpret it. Batch 1 (Aug 23 pm)
 * rolled the system out to the core marketing pages; batch 2 (compare/role/
 * spoke pages) follows his batch-1 review.
 *
 * The app (logged-in) continues to use GlobalLayout untouched.
 */
// CellCog's own Support chat (Oct 26 2026, dogfood): loaded once on logged-out public pages; never under react-snap (the crawl
// must not wait on it, nor bake it into prerendered HTML). The signed-in app comes later, with signed tokens.
// The launcher belongs to the marketing pages only (Nov 1 2026, the launcher leak: 8 of 55 chats came from app pages): leaving
// this layout for the app without a reload hides it, coming back shows it again. Every route change is told to the chat
// (CellCog('update'), the loader's single-page-app call) so the starter questions and the page the AI employee sees follow it.
// The chat counts its clicks on our server (Nov 2 2026); this site's own cookie consent decides whether that carries the chat's
// visitor id: CellCog('consent', bool) on every change (supportChatConsent: Global Privacy Control or declined analytics = false).
const SUPPORT_APP_ID = 'ccs_JdkZ3OJnrrRjbTbu';
const isSnapshot = () => typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);
const useSupportChat = (pathname, consented) => {
  useEffect(() => {
    if (isSnapshot()) return undefined;
    // The chat follows the PAGE's light/dark, not the visitor's OS (Oct 27 2026). Marketing pages keep their real face on
    // html[data-mkt-theme] (adaptive since Aug 2026); html[data-theme] stays "dark" here, so reading it opened a dark chat on
    // light pages (fixed Oct 31 2026). Tell it now (queued until the loader runs) and on every data-mkt-theme change.
    if (typeof window.CellCog !== 'function') {
      const stub = function stub(...args) { (stub.q = stub.q || []).push(args); };
      window.CellCog = stub;
    }
    const root = document.documentElement;
    const tellTheme = () => {
      const v = root.getAttribute('data-mkt-theme');
      window.CellCog('theme', v === 'dark' || v === 'light' ? v : null);
    };
    tellTheme();
    const mo = new MutationObserver(tellTheme);
    mo.observe(root, { attributes: true, attributeFilter: ['data-mkt-theme'] });
    // Step aside while a film lightbox (body.cc-modal-open) or the cookie card (body.cc-cookie-open) is open: both
    // would sit under the launcher (Oct 28 2026, Maeve's clash list).
    const body = document.body;
    const tellCover = () => window.CellCog(
      body.classList.contains('cc-modal-open') || body.classList.contains('cc-cookie-open') ? 'hide' : 'show');
    tellCover();
    const bo = new MutationObserver(tellCover);
    bo.observe(body, { attributes: true, attributeFilter: ['class'] });
    if (!document.querySelector('script[data-app-id="' + SUPPORT_APP_ID + '"]')) {
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://cellcog.ai/api/cellcog/public/support-widget/widget.js';
      s.setAttribute('data-app-id', SUPPORT_APP_ID);
      // the site's two accents as hints so the button is right before the first open (the loader makes no request until then; after an open the site's saved look wins). Must match the Site's colours in the Support app.
      s.setAttribute('data-accent', '#2e7e7e');
      s.setAttribute('data-accent-dark', '#7dc3c3');
      document.body.appendChild(s);
    }
    return () => { mo.disconnect(); bo.disconnect(); window.CellCog('hide'); };
  }, []);
  // One frame later (take 2, review N2): the loader reads document.title when told, and a page sets its title in its own
  // effect or a frame later (react-helmet), so an immediate call could send the previous page's title. A newer route change
  // or leaving the layout cancels a pending one. No requestAnimationFrame (old browsers, some tests): the next tick.
  useEffect(() => {
    if (isSnapshot() || typeof window.CellCog !== 'function') return undefined;
    const tell = () => { if (typeof window.CellCog === 'function') window.CellCog('update'); };
    if (typeof window.requestAnimationFrame === 'function') {
      const frame = window.requestAnimationFrame(tell);
      return () => { if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame); };
    }
    const timer = setTimeout(tell, 0);
    return () => clearTimeout(timer);
  }, [pathname]);
  useEffect(() => {
    if (isSnapshot() || typeof window.CellCog !== 'function') return;
    window.CellCog('consent', consented);
  }, [consented]);
};

const MarketingLayout = ({ children }) => {
  const sentinelRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const cookieConsent = useContext(CookieConsentContext);  // undefined outside the provider: the stored choice decides
  useSupportChat(pathname, supportChatConsent(cookieConsent || {}));
  const heroPage = isHeroPath(pathname);
  const isAuthPage = AUTH_PATHS.has(pathname);

  // Safari (WebKit) needs position:fixed for the marketing header instead of
  // sticky — sticky triggers a Safari-only scroll flicker ("up and down while
  // scrolling"). Tagging the shell lets the header switch to fixed (see
  // MarketingHeader.css) and lets the layout reserve the header's 64px, all
  // scoped to Safari (Chrome keeps sticky). The UA test excludes Chrome/Edge/
  // Android and Chrome/Firefox-on-iOS, which all carry "Safari" in their UA.
  const isSafari =
    typeof navigator !== 'undefined' &&
    /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);
  // Plain (non-hero) pages slide their OWN content up behind the transparent
  // header (see .is-plain-page CSS), so the page's own background fills the
  // area behind the header — no separate "black strip". The header stays
  // transparent and only gains its blur on scroll, same as hero pages.

  // Theme side effects (data-theme attribute + body backdrop) are owned by
  // ThemeContext ALONE (CEL-1359 single-writer fix). This shell only mounts
  // for logged-out visitors, and ThemeContext already forces isDarkMode=true
  // for them — so the dark attribute + #0a0b0d body are guaranteed without a
  // second writer. The old mount-pin/unmount-restore here could land a stale
  // write in the same commit as a login (the restore ref never received the
  // post-login value on an unmounting component), leaving data-theme='dark'
  // under a light antd algorithm — the mixed-theme split new light-OS
  // signups hit at /welcome and inside the app.

  // Drive the header's blurred background from the in-flow top sentinel via
  // IntersectionObserver against the viewport — the canonical, robust pattern.
  // When the sentinel (a zero-height marker at the very top of content) scrolls
  // out of the viewport, isIntersecting flips false → header gains its blur.
  // Works regardless of WHICH element scrolls (window, html, or the app content
  // area when logged in) because IO observes viewport intersection directly.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        setScrolled(!e.isIntersecting);
      },
      { threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // AUTH PAGES: engage the header veil on ANY scroll (not after ~65px).
  // The sentinel flips the veil only once it has fully left the viewport —
  // i.e. after the first ~65px of scroll. On every other page content starts
  // far below the header, so nothing reaches it inside that window. The auth
  // card sits just below the header (tight top margin), so between ~12-65px
  // of scroll it slid under a still-transparent header. This early trigger
  // closes that window; past 65px the sentinel takes over as usual.
  const [authScrolled, setAuthScrolled] = useState(false);
  useEffect(() => {
    if (!isAuthPage) {
      setAuthScrolled(false);
      return undefined;
    }
    const onScroll = () =>
      setAuthScrolled((window.scrollY || document.documentElement.scrollTop || 0) > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isAuthPage]);

  return (
    <MarketingShellContext.Provider value>
      <div className={`marketing-shell dark ${heroPage ? 'is-hero-page' : 'is-plain-page'}${isSafari ? ' mkt-safari' : ''}${isAdaptivePath(pathname) ? ' mkt-adaptive' : ''}`}>
        {/* The Vanta.js CELLS WebGL layer is GONE (Nitish, Aug 23): it pulled
            three.js + vanta from CDN and ran a GPU shader on every logged-out
            page — real mobile page-weight and SEO-vitals cost. The CSS
            vignette still carries the dark pages' backdrop; adaptive pages
            are a plain screen by design (MarketingAdaptiveTheme.css). */}
        <MarketingHeader scrolled={scrolled || authScrolled} slim={isAuthPage} />
        <div className="marketing-shell__content">
          {/* In-flow sentinel at the very top of content. When it scrolls out
              of the viewport, the header gains its blurred background. In-flow
              (not absolute) so it tracks the real scroll. */}
          <div ref={sentinelRef} className="marketing-shell__sentinel" aria-hidden="true" />
          {children}
          {isAuthPage ? <AuthSlimFooter /> : <MarketingFooter />}
        </div>
      </div>
    </MarketingShellContext.Provider>
  );
};

export default MarketingLayout;
